'use client'

import { useRouter } from 'next/navigation'
import { useEffect, type ReactNode } from 'react'
import { useCreationStore } from './store/provider'

type Props = {
    basePath: string
    children: ReactNode
}

/**
 * Client-side half of the deep-link rule. Hard requests are already bounced by
 * Django's ^create/.*$ redirect; this covers client navigations that arrive at
 * a step with no sources — a cleared store, or the back button.
 */
const RequireSources = ({ basePath, children }: Props) => {
    const router = useRouter()
    const hasSources = useCreationStore((state) => state.sources.length > 0)
    // See create-store.ts's own comment on `isNavigatingAway`: a successful
    // upload releases sources (to free canvases) via a client-side
    // router.push, which — unlike legacy's full-page navigation — does not
    // unmount this component. Without this check, that release looks
    // identical to "arrived here with no sources" and this effect's own
    // router.replace wins the race against the push already under way,
    // stranding the user on the welcome screen instead of their new GIF.
    const isNavigatingAway = useCreationStore((state) => state.isNavigatingAway)

    useEffect(() => {
        if (!hasSources && !isNavigatingAway) {
            router.replace(basePath)
        }
    }, [basePath, hasSources, isNavigatingAway, router])

    if (!hasSources && !isNavigatingAway) {
        return null
    }

    return <>{children}</>
}

export default RequireSources
